import apiClient from './apiClient';
import { config } from '../config';
import { authService } from './auth';

export interface AudioLibraryItem {
  id: string;
  user_id: string;
  mod_approved: boolean;
  is_private: boolean;
  audio_type: 'music' | 'background' | 'sound';
  name: string;
  description: string | null;
  audio_file_path: string;
  waveform_file_path: string | null;
  audio_duration_seconds: number | null;
  ai_provider: string | null;
  use_count: number;
  thumbs_up: number;
  thumbs_down: number;
  created_at: string;
  updated_at: string;
  user_rating?: 'like' | 'dislike' | null;
}

export interface AudioLibraryFilters {
  audio_type?: 'music' | 'background' | 'sound';
  search?: string;
  sort?: 'popular' | 'recent' | 'rating' | 'alphabetical' | 'my_favorites';
  page?: number;
  limit?: number;
  duration_range?: 'under_1' | '1_to_2' | '2_to_5' | '5_to_10' | 'over_10';
  my_uploads_only?: boolean;
}

export interface AudioLibraryResponse {
  items: AudioLibraryItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface UploadAudioMetadata {
  name: string;
  description?: string;
  audio_type: 'music' | 'background' | 'sound';
  is_private?: boolean;
}

class AudioLibraryService {
  private getAuthHeaders() {
    const token = authService.getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  /**
   * Get audio library items with filtering and pagination
   */
  async getAudioLibraryItems(filters: AudioLibraryFilters = {}): Promise<AudioLibraryResponse> {
    try {
      const params = new URLSearchParams();
      
      if (filters.audio_type) params.append('audio_type', filters.audio_type);
      if (filters.search) params.append('search', filters.search);
      if (filters.sort) params.append('sort', filters.sort);
      if (filters.page) params.append('page', filters.page.toString());
      if (filters.limit) params.append('limit', filters.limit.toString());
      if (filters.duration_range) params.append('duration_range', filters.duration_range);
      if (filters.my_uploads_only !== undefined) params.append('my_uploads_only', filters.my_uploads_only.toString());

      const response = await apiClient.get(
        `${config.apiUrl}/audio-library?${params.toString()}`,
        { headers: this.getAuthHeaders() }
      );
      
      return response.data;
    } catch (error) {
      console.error('Get audio library items error:', error);
      throw error;
    }
  }

  /**
   * Get a single audio library item
   */
  async getAudioLibraryItem(id: string): Promise<AudioLibraryItem> {
    try {
      const response = await apiClient.get(
        `${config.apiUrl}/audio-library/${id}`,
        { headers: this.getAuthHeaders() }
      );
      
      return response.data;
    } catch (error) {
      console.error('Get audio library item error:', error);
      throw error;
    }
  }

  /**
   * Upload new audio to library
   */
  async uploadAudioLibraryItem(file: File, metadata: UploadAudioMetadata): Promise<AudioLibraryItem> {
    try {
      const formData = new FormData();
      formData.append('audio', file);
      formData.append('name', metadata.name);
      formData.append('audio_type', metadata.audio_type);
      
      if (metadata.description) {
        formData.append('description', metadata.description);
      }
      if (metadata.is_private !== undefined) {
        formData.append('is_private', metadata.is_private.toString());
      }

      const response = await apiClient.post(
        `${config.apiUrl}/audio-library`,
        formData,
        {
          headers: {
            ...this.getAuthHeaders(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      return response.data;
    } catch (error) {
      console.error('Upload audio library item error:', error);
      throw error;
    }
  }

  /**
   * Rate an audio library item
   */
  async rateAudioLibraryItem(id: string, rating: 'like' | 'dislike'): Promise<void> {
    try {
      await apiClient.put(
        `${config.apiUrl}/audio-library/${id}/rating`,
        { rating },
        { headers: this.getAuthHeaders() }
      );
    } catch (error) {
      console.error('Rate audio library item error:', error);
      throw error;
    }
  }

  /**
   * Remove rating from an audio library item
   */
  async removeRating(id: string): Promise<void> {
    try {
      await apiClient.delete(
        `${config.apiUrl}/audio-library/${id}/rating`,
        { headers: this.getAuthHeaders() }
      );
    } catch (error) {
      console.error('Remove audio library item rating error:', error);
      throw error;
    }
  }

  /**
   * Delete an audio library item
   */
  async deleteAudioLibraryItem(id: string): Promise<void> {
    try {
      await apiClient.delete(
        `${config.apiUrl}/audio-library/${id}`,
        { headers: this.getAuthHeaders() }
      );
    } catch (error) {
      console.error('Delete audio library item error:', error);
      throw error;
    }
  }

  /**
   * Get audio URL for playing from S3
   */
  getAudioUrl(item: AudioLibraryItem): string {
    // The audio_file_path now contains the S3 key
    return `https://${config.awsS3LibraryBucket}.s3.amazonaws.com/${item.audio_file_path}`;
  }

  /**
   * Get waveform URL from S3
   */
  getWaveformUrl(item: AudioLibraryItem): string | null {
    if (!item.waveform_file_path) return null;
    // The waveform_file_path now contains the S3 key
    return `https://${config.awsS3WaveformsBucket}.s3.amazonaws.com/${item.waveform_file_path}`;
  }

  
  /**
   * Validate audio file before upload
   */
  validateAudioFile(file: File): { isValid: boolean; error?: string } {
    const maxSize = 50 * 1024 * 1024; // 50MB
    const allowedTypes = ['audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/mp4', 'audio/webm'];
    
    if (file.size > maxSize) {
      return { isValid: false, error: 'File size must be less than 50MB' };
    }
    
    if (!allowedTypes.includes(file.type)) {
      return { isValid: false, error: 'File must be an audio file (MP3, WAV, OGG, MP4, or WebM)' };
    }
    
    return { isValid: true };
  }

  /**
   * Get audio duration from file
   */
  async getAudioDuration(file: File): Promise<number> {
    return new Promise((resolve) => {
      const audio = new Audio();
      const url = URL.createObjectURL(file);
      
      audio.addEventListener('loadedmetadata', () => {
        URL.revokeObjectURL(url);
        resolve(audio.duration);
      });
      
      audio.addEventListener('error', () => {
        URL.revokeObjectURL(url);
        resolve(0);
      });
      
      audio.src = url;
    });
  }
}

export const audioLibraryService = new AudioLibraryService();